import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { ArrowDown } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';

import AutoResizableTextarea from './AutoResizableTextarea';
import { DataState } from '../context/DataProvider';
import MessageImages from './chat/MessageImages';
import ToolFileUploadUI from './chat/ToolFileUploadUI';
import ToolResponseApprovalUI from './chat/ToolResponseApprovalUI';
import ThinkingPanel from './chat/ThinkingPanel';
import ToolCallsUI from './chat/ToolCallsUI';
import TextReasoningDropdown from './chat/TextReasoningDropdown';
import ActionTrackerEmbedded from './chat/ActionTrackerEmbedded';
import MessageAttachments from './chat/MessageAttachments';
import ToolsResponseUI from './chat/ToolsResponseUI';
import InlineTaskPlan from './chat/InlineTaskPlan';
import MarkdownRenderer from './MarkdownRenderer';

const MemoMarkdown = React.memo(MarkdownRenderer);

// Parse <change_request> XML out of a user message, returning capsule data + leftover text
const parseCRContext = (content) => {
  if (!content || typeof content !== 'string') return null;
  const match = content.match(
    /<change_request\s[^>]*>([\s\S]*?)<\/change_request>/,
  );
  if (!match) return null;
  const idMatch = content.match(/id="([^"]+)"/);
  const sectionMatch = content.match(/section="([^"]+)"/);
  let title = idMatch?.[1] || 'Change Request';
  try {
    const data = JSON.parse(match[1].trim());
    if (typeof data.title === 'string' && data.title) title = data.title;
  } catch {}
  const tagStart = content.indexOf('<change_request');
  const tagEnd = tagStart + match[0].length;
  return {
    id: idMatch?.[1] || 'CR',
    title,
    section: sectionMatch?.[1] || null,
    rest: content
      .slice(tagEnd)
      .replace(/^\s*\n+/, '')
      .trim(),
  };
};

const CRContextCapsule = ({ crContext }) => (
  <div className="mb-1.5 flex items-center">
    <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-medium">
      <span className="flex-shrink-0 font-mono font-bold text-amber-400">
        {crContext.id}
      </span>
      {crContext.section && (
        <>
          <span className="flex-shrink-0 text-amber-500/50">·</span>
          <span className="flex-shrink-0 text-amber-300/70">
            {crContext.section}
          </span>
        </>
      )}
      <span className="truncate text-amber-200/80">
        {String(crContext.title)}
      </span>
    </div>
  </div>
);

// Renders a single string/array message body, replacing CR XML with a capsule
function ChatMessageContent({ chat, userGoogleId }) {
  // Resolve the raw text from either string or array content
  let rawText = null;
  if (chat.content && typeof chat.content === 'string') {
    rawText = chat.content.includes('TASK STATUS:')
      ? chat.content.split('TASK STATUS:')[0].trim()
      : chat.content;
  } else if (
    chat.content &&
    Array.isArray(chat.content) &&
    chat.content.length > 0 &&
    chat.content[0] &&
    chat.content[0].text
  ) {
    rawText = chat.content[0].text.includes('TASK STATUS:')
      ? chat.content[0].text.split('TASK STATUS:')[0].trim()
      : chat.content[0].text;
  }

  if (!rawText) return null;

  const crCtx = chat.role === 'user' ? parseCRContext(rawText) : null;

  if (crCtx) {
    return (
      <>
        <CRContextCapsule crContext={crCtx} />
        {crCtx.rest ? (
          <MemoMarkdown
            isAssistant={false}
            attachments={chat.attachments}
            userGoogleId={userGoogleId}
            markdown={crCtx.rest}
          />
        ) : null}
      </>
    );
  }

  return (
    <MemoMarkdown
      isAssistant={chat.role !== 'user'}
      attachments={chat.attachments}
      userGoogleId={userGoogleId}
      markdown={rawText}
    />
  );
}

// Stable key helper
// Prefers a message-level ID so React never re-mounts stable rows.
// Falls back to role+index only for synthetic rows (reason, tool_calls_ui, streamingResponse).
const getChatKey = (chat, index) => {
  if (chat?._id) return chat._id;
  if (chat?.tool_call_id) return `tool-${chat.tool_call_id}`;
  if (chat?.role === 'reason') return `reason-${index}`;
  if (chat?.role === 'tool_calls_ui') return `tcu-${index}`;
  // For assistant messages without _id, use index to guarantee uniqueness.
  // Previously all such messages shared the static key "streaming-response",
  // which caused React to duplicate / omit rows when more than one existed.
  if (chat?.role === 'assistant' && !chat?._id) return `assistant-${index}`;
  return `chat-${index}`;
};

// Individual chat row — memoised so the virtualizer
// only re-renders rows whose props actually changed.
const ChatRow = React.memo(function ChatRow({
  chat,
  userInfo,
  userGoogleId,
  toolCallApprovalList,
  currentToolResponseForReference,
  isStreaming: isStreamingProp,
  isReasoning,
  onApprove,
  onDecline,
}) {
  // tool_calls_ui rows are injected for status tracking but render nothing
  // in ChatRow — skip them entirely to avoid empty gaps.
  if (chat.role === 'tool_calls_ui') return null;

  const hasContent =
    ((chat.type && chat.role === 'bot') ||
      (chat.content && typeof chat.content === 'string') ||
      (chat.reasoning && chat.role === 'assistant' && isReasoning) ||
      (chat.content &&
        Array.isArray(chat.content) &&
        chat.content.length !== 0 &&
        chat.content[0] &&
        chat.content[0].text)) &&
    chat.role !== 'tool';

  return (
    <>
      {hasContent && (
        <div className="mb-3 flex flex-col gap-1">
          <div
            className={`flex w-full flex-col gap-2 ${chat.role === 'user' ? 'items-end' : 'items-start'}`}
          >
            {chat.role === 'user' && Array.isArray(chat.content) && (
              <div className="w-full">
                <MessageImages content={chat.content} />
              </div>
            )}
            {chat.attachments && chat.attachments.length > 0 && (
              <div className="w-full">
                <MessageAttachments attachments={chat.attachments} />
              </div>
            )}
          </div>
          <div
            className={`flex gap-1 ${chat?.isSubtask ? 'w-[90%] justify-end' : 'w-full'}`}
          >
            <div
              className={`flex gap-1 ${userInfo && chat.role === 'user' ? 'ml-auto flex-row-reverse ' : 'mr-auto w-full flex-row'} items-start`}
            >
              <div className="min-w-0 flex-1">
                <div
                  className={`rounded-xl ${chat.role !== 'user' ? 'bg-[#0E0E0E] bg-opacity-0 px-1 py-1.5' : 'ml-auto bg-[#2F2F2F] px-2.5 py-1.5'}`}
                >
                  <div className="break-words">
                    {chat.reasoning &&
                    chat.role === 'assistant' &&
                    isReasoning ? (
                      <TextReasoningDropdown
                        reasoningText={chat.reasoning}
                        isReasoning={chat.is_reasoning}
                      />
                    ) : null}
                    {chat.content &&
                      (typeof chat.content === 'string' ||
                        (Array.isArray(chat.content) &&
                          chat.content.length > 0 &&
                          chat.content[0].text)) && (
                        <ChatMessageContent
                          chat={chat}
                          userGoogleId={userGoogleId}
                        />
                      )}
                    {chat.role === 'bot' && chat.type === 'session' && (
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-white p-0.5">
                          <img
                            src={'https://oqlous.inqbator.in/assets/oqlous.svg'}
                            alt="Trigger icon"
                          />
                        </div>
                        <MemoMarkdown
                          markdown={`${chat.trigger_name}`}
                          userGoogleId={userGoogleId}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {chat.role === 'tool' && <ToolsResponseUI tool={chat} />}
      {chat.role === 'assistant' && Array.isArray(chat.tool_calls) && (
        <ActionTrackerEmbedded
          currentToolsStatus={chat.tool_calls}
          toolCallApprovalList={toolCallApprovalList}
          onApprove={onApprove}
          onDecline={onDecline}
        />
      )}
      {chat.role === 'assistant' && Array.isArray(chat.tool_calls) && (
        <ToolCallsUI
          currentToolResponseForReference={currentToolResponseForReference}
          tool_calls={chat.tool_calls}
        />
      )}
      {chat.role === 'reason' && !isStreamingProp && isReasoning && (
        <ThinkingPanel reasonCalls={chat.tool_calls} />
      )}
    </>
  );
});

const ChatList = ({
  isDraggingOver = false,
  tempImages = [],
  setTempImages = () => {},
  justUploadedImages = false,
  setJustUploadedImages = () => {},
  processAndUploadFiles = () => {},
}) => {
  //context
  const {
    conversations,
    input,
    setInput,
    messageContainerRef,
    streamingResponse,
    handleStreamSend,
    userInfo,
    userGoogleId,
    setToolCallApprovalList,
    toolCallApprovalList,
    currentToolResults,
    setCurrentToolResults,
    currentReasoningCalls,
    currentReasoningToolStreamingData,
    currentToolResponseForReference,
    currentToolCallStatusMap,
    setScrollToToolId,
    scrollToToolId,
    isStreaming,
    hasPendingToolCalls,
    isReasoning,
    statusMessage,
    handleFunctionCall,
  } = DataState();

  //states
  const [disapprovalReasons, setDisapprovalReasons] = useState({});
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Per-tool execution accumulator: keyed by toolCallId, value = handleFunctionCall result
  const pendingApprovalResultsRef = useRef({});
  // Always-current snapshot of toolCallApprovalList to avoid stale closures in async callbacks
  const toolCallApprovalListRef = useRef(toolCallApprovalList);

  // The scroll container — wired to both the virtualizer and messageContainerRef
  const scrollContainerRef = useRef(null);

  // Keep ref current so async callbacks always see the latest approval list
  useEffect(() => {
    toolCallApprovalListRef.current = toolCallApprovalList;
  }, [toolCallApprovalList]);

  // Called after each tool result arrives; fires LLM when every tool has a result
  const checkAndFinalizeBatch = useCallback(() => {
    const list = toolCallApprovalListRef.current;
    if (list.length === 0) return;
    const allDone = list.every((t) =>
      Object.prototype.hasOwnProperty.call(
        pendingApprovalResultsRef.current,
        t.id,
      ),
    );
    if (!allDone) return;

    const allResults = list.map((t) => ({
      ...(pendingApprovalResultsRef.current[t.id] || {}),
      approved: true, // required for the hasTaskResults effect in DataProvider
    }));
    setCurrentToolResults(allResults);
    setToolCallApprovalList([]);
    pendingApprovalResultsRef.current = {};
  }, [setCurrentToolResults, setToolCallApprovalList]);

  // Memoised callbacks

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleApprove = useCallback(
    (toolCallId, finalToolCall) => {
      const toolToExecute = toolCallApprovalList.find(
        (t) => t.id === toolCallId,
      );
      if (!toolToExecute) return;

      const targetTool = finalToolCall
        ? { ...finalToolCall, approved: true }
        : { ...toolToExecute, approved: true };

      setToolCallApprovalList((prevList) =>
        prevList.map((t) => (t.id === toolCallId ? targetTool : t)),
      );

      // Execute immediately; don't block on other tools
      handleFunctionCall(targetTool).then((result) => {
        pendingApprovalResultsRef.current[toolCallId] = result || {};
        checkAndFinalizeBatch();
      });
    },
    [
      toolCallApprovalList,
      handleFunctionCall,
      setToolCallApprovalList,
      checkAndFinalizeBatch,
    ],
  );

  const handleDisapprove = useCallback(
    (toolCallId, directReason) => {
      const reason = directReason || disapprovalReasons[toolCallId]?.trim();
      if (!reason) {
        alert('Please provide a reason for disapproval.');
        return;
      }

      const toolToExecute = toolCallApprovalList.find(
        (t) => t.id === toolCallId,
      );
      if (!toolToExecute) return;

      const disapprovedTool = {
        ...toolToExecute,
        approved: true,
        disapproved: true,
        disapprovalReason: reason,
      };
      setToolCallApprovalList((prevList) =>
        prevList.map((t) => (t.id === toolCallId ? disapprovedTool : t)),
      );
      setDisapprovalReasons((prev) => ({ ...prev, [toolCallId]: '' }));

      // Execute denial immediately; don't block on other tools
      handleFunctionCall(disapprovedTool).then((result) => {
        pendingApprovalResultsRef.current[toolCallId] = result || {};
        checkAndFinalizeBatch();
      });
    },
    [
      disapprovalReasons,
      toolCallApprovalList,
      handleFunctionCall,
      setToolCallApprovalList,
      checkAndFinalizeBatch,
    ],
  );

  // --- File Upload Handlers (mirrors approval pattern) ---
  const handleFileUploaded = useCallback(
    (toolCallId, uploadResult) => {
      const toolToExecute = toolCallApprovalList.find(
        (t) => t.id === toolCallId,
      );
      if (!toolToExecute) return;

      // For Jira attach_file mode, onComplete passes { uploadResult, updatedToolCall }
      // where updatedToolCall has the s3_filename already injected into the payload.
      // Fall back to the original tool for Shiprocket CSV mode.
      const resolvedTool = uploadResult?.updatedToolCall || toolToExecute;

      const updatedTool = {
        ...resolvedTool,
        fileUploaded: true,
        fileUploadResult: JSON.stringify(uploadResult),
        approved: true,
      };

      setToolCallApprovalList((prevList) =>
        prevList.map((t) => (t.id === toolCallId ? updatedTool : t)),
      );

      // Execute immediately
      handleFunctionCall(updatedTool).then((result) => {
        pendingApprovalResultsRef.current[toolCallId] = result || {};
        checkAndFinalizeBatch();
      });
    },
    [
      toolCallApprovalList,
      handleFunctionCall,
      setToolCallApprovalList,
      checkAndFinalizeBatch,
    ],
  );

  const handleFileDismissed = useCallback(
    (toolCallId) => {
      const toolToExecute = toolCallApprovalList.find(
        (t) => t.id === toolCallId,
      );
      if (!toolToExecute) return;

      const updatedTool = {
        ...toolToExecute,
        fileUploaded: true,
        approved: true,
        dismissed: true,
      };
      setToolCallApprovalList((prevList) =>
        prevList.map((t) => (t.id === toolCallId ? updatedTool : t)),
      );

      // Execute immediately; don't block on other tools being approved first
      handleFunctionCall(updatedTool).then((result) => {
        pendingApprovalResultsRef.current[toolCallId] = result || {};
        checkAndFinalizeBatch();
      });
    },
    [
      toolCallApprovalList,
      handleFunctionCall,
      setToolCallApprovalList,
      checkAndFinalizeBatch,
    ],
  );

  const handleSend = useCallback(
    (message) => {
      if (message) {
        setInput(message);
        // React state updates are async, so handleStreamSend might need to read message directly
        // but handleStreamSend inside DataProvider uses `input` state.
        // To fix the immediate state use, we'll configure handleStreamSend to optionally accept the input string.
      }
      handleStreamSend(null, message);
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    },
    [setInput, scrollToBottom, handleStreamSend],
  );

  // allConversations — base structure that changes infrequently
  // Note: currentReasoningToolStreamingData is RAF-throttled in DataProvider
  // so this memo recalculates at most ~60fps, not per-chunk.

  const baseConversations = useMemo(() => {
    const result = [];
    let lastUserChatIndex = -1;
    conversations.forEach((conversation, i) => {
      if (conversation?.role === 'user') {
        lastUserChatIndex = i;
      } else if (conversation?.role === 'assistant') {
        if (
          conversation?.tool_calls &&
          Array.isArray(conversation?.tool_calls) &&
          conversation?.tool_calls.length > 0
        ) {
          conversation = {
            ...conversation,
            tool_calls: conversation.tool_calls.map((toolCall) =>
              currentToolCallStatusMap?.get(toolCall.id)
                ? {
                    ...toolCall,
                    status: currentToolCallStatusMap.get(toolCall.id).status,
                    running_phase: currentToolCallStatusMap.get(toolCall.id)
                      .running_phase,
                  }
                : toolCall,
            ),
          };
        }
      }
      result.push(conversation);
    });
    if (currentReasoningCalls?.length > 0 && lastUserChatIndex !== -1) {
      result.splice(lastUserChatIndex + 1, 0, {
        role: 'reason',
        tool_calls: currentReasoningCalls,
        _hasReasoningSlot: true,
      });
      lastUserChatIndex = lastUserChatIndex + 1;
    }
    return { result, lastUserChatIndex };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, currentReasoningCalls, currentToolCallStatusMap]);

  // Merge streaming reasoning data into base conversations only when it changes.
  // This is a lightweight pass — only touches the reason row.
  const allConversations = useMemo(() => {
    const { result, lastUserChatIndex } = baseConversations;
    if (!currentReasoningToolStreamingData) return result;

    // If a reason row already exists, append streaming data to it
    const hasReasonRow = result.some((conv) => conv._hasReasoningSlot);
    if (hasReasonRow) {
      return result.map((conv) => {
        if (conv._hasReasoningSlot) {
          return {
            ...conv,
            tool_calls: [...conv.tool_calls, currentReasoningToolStreamingData],
          };
        }
        return conv;
      });
    }

    // No reason row yet but streaming data exists — inject one
    if (lastUserChatIndex !== -1) {
      const merged = [...result];
      merged.splice(lastUserChatIndex + 1, 0, {
        role: 'reason',
        tool_calls: [currentReasoningToolStreamingData],
      });
      return merged;
    }

    return result;
  }, [baseConversations, currentReasoningToolStreamingData]);

  // Combine allConversations + streamingResponse into one flat array for the virtualizer.
  // Only append streamingResponse if it actually has content — otherwise the finalized
  // assistant message (already in allConversations) and the empty streaming placeholder
  // both lack _id and get the same "streaming-response" key, causing React key conflicts.
  const virtualItems = useMemo(() => {
    const hasStreamingContent =
      streamingResponse &&
      streamingResponse.role === 'assistant' &&
      ((streamingResponse.content && streamingResponse.content.length > 0) ||
        (streamingResponse.tool_calls &&
          streamingResponse.tool_calls.length > 0) ||
        streamingResponse.reasoning);
    if (hasStreamingContent) {
      return [...allConversations, streamingResponse];
    }
    return allConversations;
  }, [allConversations, streamingResponse]);

  // Virtualizer
  const virtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => scrollContainerRef.current,
    // Initial estimate; the virtualizer will measure and correct after render
    estimateSize: () => 50,
    // Enable dynamic height measurement
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5, // Render 5 extra items above/below the viewport for smooth scroll
    getItemKey: (index) => getChatKey(virtualItems[index], index),
  });

  // Scroll-to-tool helper

  useEffect(() => {
    if (!scrollToToolId) return;
    const element = document.querySelector(
      `[data-tool-id='${scrollToToolId}']`,
    );
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-scroll');
      setTimeout(() => {
        element.classList.remove('highlight-scroll');
        setScrollToToolId(null);
      }, 1500);
    } else {
      setScrollToToolId(null);
    }
  }, [scrollToToolId, setScrollToToolId]);

  // Show scroll-to-bottom button

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollButton(distanceFromBottom > 150);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (virtualItems.length > 0) {
      // Only auto-scroll if user is already near the bottom
      const container = scrollContainerRef.current;
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < 300) {
        virtualizer.scrollToIndex(virtualItems.length - 1, {
          align: 'end',
          behavior: 'smooth',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualItems.length]);

  return (
    <>
      <div className="relative grid h-full w-full grid-rows-[1fr_auto]">
        <div
          ref={(el) => {
            scrollContainerRef.current = el;
            // Also honour the external messageContainerRef from context
            if (messageContainerRef) {
              if (typeof messageContainerRef === 'function') {
                messageContainerRef(el);
              } else {
                messageContainerRef.current = el;
              }
            }
          }}
          className="transparent-scrollbar h-full w-full overflow-y-auto"
        >
          {/* Virtual scroll container */}
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const chat = virtualItems[virtualRow.index];
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div
                    className={`mx-auto w-full max-w-6xl px-4 pb-1 ${virtualRow.index === 0 ? 'pt-6' : ''}`}
                  >
                    <ChatRow
                      chat={chat}
                      userInfo={userInfo}
                      userGoogleId={userGoogleId}
                      toolCallApprovalList={toolCallApprovalList}
                      currentToolResponseForReference={
                        currentToolResponseForReference
                      }
                      isStreaming={isStreaming && chat.role === 'reason'}
                      isReasoning={isReasoning}
                      onApprove={handleApprove}
                      onDecline={(toolCallId, reason) =>
                        handleDisapprove(
                          toolCallId,
                          reason || 'Declined by user',
                        )
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mx-auto w-full max-w-6xl px-4">
            {/* These are always rendered after the virtual list — no change in behaviour */}
            {isStreaming && <InlineTaskPlan />}
            {isStreaming &&
              isReasoning &&
              !(
                streamingResponse?.content?.length > 0 &&
                streamingResponse.content[0]?.text
              ) && (
                <div className="mb-3 mr-auto flex w-full flex-row items-start gap-1">
                  <div className="min-w-0 flex-1">
                    <ThinkingPanel
                      reasonCalls={
                        currentReasoningToolStreamingData
                          ? [
                              ...currentReasoningCalls,
                              currentReasoningToolStreamingData,
                            ]
                          : currentReasoningCalls.length > 0
                            ? currentReasoningCalls
                            : []
                      }
                      isStreaming={true}
                    />
                  </div>
                </div>
              )}
            {(isStreaming || hasPendingToolCalls) &&
              statusMessage &&
              !(
                (streamingResponse?.content?.length > 0 &&
                  streamingResponse?.content?.[0]?.text?.trim()?.length > 0) ||
                (isReasoning &&
                  streamingResponse?.reasoning?.trim()?.length > 0) ||
                (isReasoning && currentReasoningCalls?.length > 0) ||
                streamingResponse?.tool_calls?.some(
                  (tc) =>
                    tc.function?.name &&
                    tc.function.name !== 'record_thought_process',
                )
              ) && (
                <div className="mb-3 mr-auto flex w-full flex-row items-start gap-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-1 rounded-xl bg-transparent px-1 py-1.5">
                      <div className="flex items-center gap-2 text-sm font-bold text-zinc-200">
                        <div className="w-6"></div>
                        <span className="font-bold">Oqlous AI</span>
                      </div>
                      <div className="flex items-center gap-2 font-bold text-zinc-200">
                        <img
                          src="../assets/logo.png"
                          className="w-6 animate-rotatePause"
                          alt="logo"
                        />
                        <span>{statusMessage}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            <ToolFileUploadUI
              fileUploadList={toolCallApprovalList}
              onFileUploaded={handleFileUploaded}
              onFileDismissed={handleFileDismissed}
            />
            <ToolResponseApprovalUI
              currentToolResults={currentToolResults}
              setCurrentToolResults={setCurrentToolResults}
            />
          </div>
        </div>

        <div className="relative min-w-0 flex-shrink-0">
          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              className="animate-fade-in-fast absolute -top-14 left-1/2 z-50 -translate-x-1/2 rounded-full border border-zinc-600 bg-zinc-800 p-2.5 text-white shadow-lg transition-all duration-200 hover:bg-zinc-700"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          )}
          <AutoResizableTextarea
            setInput={setInput}
            input={input}
            handleSend={handleSend}
            isDraggingOver={isDraggingOver}
            tempImages={tempImages}
            justUploadedImages={justUploadedImages}
            setJustUploadedImages={setJustUploadedImages}
            processAndUploadFiles={processAndUploadFiles}
            setTempImages={setTempImages}
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(ChatList);
